/**
 * Two things a controller needs when a live connection's transports look unhealthy —
 * kept isolated from HOW the controller reacts to them:
 *
 *   - `needsRejoin(transports)` — pure predicate, no side effects, no I/O.
 *   - `restartIce(ctx, transports)` — the actual ICE-restart mechanics, tried
 *     independently on send and recv (see below for why independently).
 *
 * This file deliberately does NOT decide "restart ICE, and fall back to a full rejoin
 * if that's not enough" — that decision needs to close the OLD transports and open NEW
 * ones, i.e. it needs both `teardown-phase.ts` and `connect-phase.ts`, which makes it
 * the controller's job (see `operator-session.ts`'s `recover()`), not this phase's. A
 * recovery decision that could reach into other phases wouldn't be isolated at all.
 *
 * Background for readers new to WebRTC: "ICE restart" asks the browser to renegotiate
 * network connectivity for an EXISTING transport without recreating it — cheaper than a
 * full rejoin, but not always sufficient (e.g. if the transport itself is already
 * closed). `restartIce` tries it independently on the send and recv transports, because
 * the caller specifically needs to know whether *recv* came back (that's the one
 * carrying video) — send-only recovery still leaves the operator watching a frozen
 * frame.
 */

import type { types as Mediasoup } from "mediasoup-client";

import type { ProtooClient } from "./protoo-client";
import type { PhaseTransports } from "./teardown-phase";

export function needsRejoin(transports: PhaseTransports | null): boolean {
  const { recvTransport: recv, sendTransport: send } = transports ?? {};
  if (!recv && !send) return true;
  const dead = (t: Mediasoup.Transport | undefined) =>
    !t || t.closed || t.connectionState === "failed" || t.connectionState === "closed";
  return dead(recv) || dead(send);
}

export interface RestartIceContext {
  protoo: ProtooClient;
  appendDiag: (s: string, opts?: { level?: string }) => void;
}

export interface RestartIceOutcome {
  send: boolean;
  recv: boolean;
}

/**
 * Restart ICE on the send and recv transports independently — a failure on one must
 * not be masked by success on the other, since the caller needs to know specifically
 * whether *recv* (video) came back, not just "something did".
 */
export async function restartIce(
  ctx: RestartIceContext,
  transports: PhaseTransports | null
): Promise<RestartIceOutcome> {
  const { protoo, appendDiag } = ctx;
  const { sendTransport, recvTransport } = transports ?? {};
  if (!protoo?.isConnected) return { send: false, recv: false };
  appendDiag("[reconnect] restartIce…", { level: "warn" });

  async function restartOne(transport: Mediasoup.Transport | undefined, role: "send" | "recv") {
    if (!transport || transport.closed) return false;
    try {
      const { iceParameters } = await protoo.request("restartIce", { transportId: transport.id });
      await transport.restartIce({ iceParameters });
      return true;
    } catch (err) {
      appendDiag(`[reconnect] restartIce(${role}) failed: ${err}`, { level: "warn" });
      return false;
    }
  }

  const send = await restartOne(sendTransport, "send");
  const recv = await restartOne(recvTransport, "recv");

  if (send || recv) {
    appendDiag(`[reconnect] restartIce done (send=${send} recv=${recv})`);
  } else {
    appendDiag("[reconnect] restartIce: no live transports restarted", { level: "debug" });
  }
  return { send, recv };
}
